import { DeviceType } from '$lib/constants';
import logger from '$lib/logger';
import { sanFranciscanRTUSettings } from '$lib/PersistedState.svelte';
import { fahrenheitToCelsius } from '$lib/RLogUtils';
import { modbusDebugEnabled } from '$lib/State.svelte';
import type { SerialPortInfo } from '$lib/types/Device';
import { invoke } from '@tauri-apps/api/core';
import { BaseDevice } from '../base';

class RTUClientError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'RTUClientError';
  }
}

interface SanFranciscanRTUReading {
  bean: number;
  environment: number;
}

export class SanFranciscanRTUDevice extends BaseDevice {
  type: DeviceType = DeviceType.SAN_FRANCISCAN_RTU;
  private portName: string = ''; // Will be set from persisted state or constructor
  private slaveId: number = 1; // Default slave ID

  // Register mappings for Watlow PM6 controller via RTU
  // Each temperature value spans two 16-bit registers (32-bit IEEE 754 float)
  private static readonly REGISTERS = {
    // Analog Inputs (32-bit floats) - matching Python script
    ET_REGISTER: 360, // Environmental Temperature (channel 1)
    BT_REGISTER: 450 // Bean Temperature (channel 2)
  };

  protected async onConnect(): Promise<boolean> {
    this.portName = sanFranciscanRTUSettings.current.portName || '';
    this.slaveId = sanFranciscanRTUSettings.current.slaveId || 1;

    try {
      // Validate port name before attempting connection
      if (!this.portName || this.portName.trim() === '') {
        logger.error('Port name is not set. Cannot connect to RTU device.');
        return false;
      }

      logger.info(`Attempting to connect to RTU device on port: ${this.portName}`);

      // Define the registers we need to monitor for background reading
      const registersToMonitor = [
        SanFranciscanRTUDevice.REGISTERS.ET_REGISTER, // 360 - Environmental Temperature
        SanFranciscanRTUDevice.REGISTERS.ET_REGISTER + 1, // 361 - Environmental Temperature (MSW)
        SanFranciscanRTUDevice.REGISTERS.BT_REGISTER, // 450 - Bean Temperature
        SanFranciscanRTUDevice.REGISTERS.BT_REGISTER + 1 // 451 - Bean Temperature (MSW)
      ];

      // Log the exact parameters being sent to Tauri
      const connectParams = {
        portPath: this.portName,
        baudRate: 9600,
        slaveId: this.slaveId,
        registersToMonitor: registersToMonitor
      };

      logger.debug('RTU connect parameters:', connectParams);

      // Connect to RTU device and start background monitoring
      await invoke('connect_rtu', connectParams);

      logger.info(
        `Connected to San Franciscan RTU device on ${this.portName} with background monitoring for registers: ${registersToMonitor.join(', ')}`
      );
      return true;
    } catch (error) {
      logger.error(`Failed to connect to San Franciscan RTU device: ${error}`);
      return false;
    }
  }

  protected onDisconnect(): void {
    invoke('disconnect_rtu', {
      portPath: this.portName,
      baudRate: 9600,
      slaveId: this.slaveId
    }).catch((error) => logger.warn(`RTU disconnect error: ${error}`));
  }

  protected async updateReadings(): Promise<void> {
    try {
      const readings = await this.readAllValues();

      const bean = fahrenheitToCelsius(readings.bean);
      const environment = fahrenheitToCelsius(readings.environment);

      if (modbusDebugEnabled.current === true) {
        logger.debug('DEVICE_SANFRANCISCAN_RTU_READINGS', { readings, bean, environment });
      }

      // San Franciscan returns temperatures in °F, convert to °C for consistency
      // Only two temperature channels: Bean (TC1) and Environment (TC2)
      this.localReadings = [
        bean, // TC1: Bean Temperature
        environment, // TC2: Environment Temperature
        null, // TC3: Not used
        null // TC4: Not used
      ];
    } catch (error) {
      if (error instanceof RTUClientError) {
        logger.error(`RTU error: ${error.message}`);
      } else {
        logger.error(`Unknown error: ${error}`);
      }
      throw error;
    }
  }

  private async readAllValues(): Promise<SanFranciscanRTUReading> {
    try {
      // Read both temperature values in parallel using RTU commands
      //
      logger.debug('Reading all values from San Franciscan RTU device');
      const [environmentReading, beanReading] = await Promise.all([
        this.readFloatRegisterRTU(SanFranciscanRTUDevice.REGISTERS.ET_REGISTER),
        this.readFloatRegisterRTU(SanFranciscanRTUDevice.REGISTERS.BT_REGISTER)
      ]);
      logger.debug('Read all values from San Franciscan RTU device');

      return {
        environment: environmentReading,
        bean: beanReading
      };
    } catch (err: any) {
      const regex = /RTU connection not established/i;
      if (regex.test(err)) {
        throw new RTUClientError('Could not establish RTU connection');
      } else {
        logger.warn(`RTU reading error: ${err}`);
      }

      return {
        environment: 0,
        bean: 0
      };
    }
  }

  /**
   * Read a 32-bit float value from two consecutive RTU registers using cached values when available
   * @param startRegister Starting register address
   * @returns Float value
   */
  private async readFloatRegisterRTU(startRegister: number): Promise<number> {
    try {
      // First try to get cached values from the background read loop
      const cachedValues = await this.getCachedRegisterValues([startRegister, startRegister + 1]);

      if (
        cachedValues.size === 2 &&
        cachedValues.has(startRegister) &&
        cachedValues.has(startRegister + 1)
      ) {
        const reg1 = cachedValues.get(startRegister)!;
        const reg2 = cachedValues.get(startRegister + 1)!;

        if (modbusDebugEnabled.current === true) {
          const hexValues = SanFranciscanRTUDevice.formatRegistersHex(reg1, reg2);
          logger.debug(
            `Using cached values for register ${startRegister}: reg1=${reg1}, reg2=${reg2} (hex: ${hexValues})`
          );
        }

        return SanFranciscanRTUDevice.convertToFloat(reg1, reg2);
      }

      // Fallback to direct read if no cached values available
      if (modbusDebugEnabled.current === true) {
        logger.debug(
          `No cached values available for register ${startRegister}, performing direct read`
        );
      }

      const results: number[] = await invoke('read_rtu', {
        portPath: this.portName,
        baudRate: 9600,
        slaveId: this.slaveId,
        reg: startRegister,
        count: 2
      });

      if (modbusDebugEnabled.current === true) {
        const hexValues = SanFranciscanRTUDevice.formatRegistersHex(results[0], results[1]);
        logger.debug(
          `Direct read values for register ${startRegister}: reg1=${results[0]}, reg2=${results[1]} (hex: ${hexValues})`
        );
      }

      return SanFranciscanRTUDevice.convertToFloat(results[0], results[1]);
    } catch (error) {
      logger.error(`Failed to read RTU register ${startRegister}: ${error}`);
      throw error;
    }
  }

  /**
   * Get cached register values from the background read loop
   * @param registers Array of register addresses to retrieve
   * @returns Map of register -> value
   */
  private async getCachedRegisterValues(registers: number[]): Promise<Map<number, number>> {
    try {
      const result = (await invoke('get_rtu_latest_values', {
        portPath: this.portName,
        baudRate: 9600,
        slaveId: this.slaveId,
        registers: registers
      })) as Record<number, number>;

      // Convert the result to a Map for easier handling
      const cachedValues = new Map<number, number>();
      for (const [reg, value] of Object.entries(result)) {
        cachedValues.set(parseInt(reg), value);
      }

      return cachedValues;
    } catch (error) {
      logger.debug(`Failed to get cached register values: ${error}`);
      return new Map(); // Return empty map on error
    }
  }

  /**
   * Alternative method using the direct temperature reading function
   * @param register Register address
   * @returns Temperature value
   */
  private async readTemperatureRTU(register: number): Promise<number> {
    try {
      // Use the new RTU read command to get 2 registers (32-bit float)
      const results: number[] = await invoke('read_rtu', {
        portPath: this.portName,
        baudRate: 9600,
        slaveId: this.slaveId,
        reg: register,
        count: 2
      });

      // Convert the two 16-bit values to a 32-bit float
      return SanFranciscanRTUDevice.convertToFloat(results[0], results[1]);
    } catch (error) {
      logger.error(`Failed to read RTU temperature from register ${register}: ${error}`);
      throw error;
    }
  }

  /**
   * Read both temperature channels from Watlow PM6.
   * Similar to the Python script implementation.
   *
   * Returns:
   *   Tuple of (ET temperature, BT temperature)
   */
  public async readTemperatures(): Promise<[number, number]> {
    try {
      // Read both temperatures in parallel
      const [etTemp, btTemp] = await Promise.all([
        this.readTemperatureRTU(SanFranciscanRTUDevice.REGISTERS.ET_REGISTER),
        this.readTemperatureRTU(SanFranciscanRTUDevice.REGISTERS.BT_REGISTER)
      ]);

      return [etTemp, btTemp];
    } catch (error) {
      logger.error(`Failed to read temperatures: ${error}`);
      throw error;
    }
  }

  /**
   * Convert two 16-bit RTU registers to a 32-bit IEEE 754 float
   * Watlow PM6 uses big-endian format (MSW first, LSW second)
   * IEEE 754 format: 0x42960000 = 75.0°F
   *
   * This matches the Python script implementation:
   * struct.pack(">HH", reg1, reg2) -> struct.unpack(">f", bytes_data)[0]
   */
  static convertToFloat(reg1: number, reg2: number): number {
    const buffer = new ArrayBuffer(4);
    const view = new DataView(buffer);

    // Big-endian format: MSW first, LSW second (matching Python struct.pack(">HH"))
    // reg1 = MSW (Most Significant Word), reg2 = LSW (Least Significant Word)
    view.setUint16(0, reg1, false); // Big endian for MSW
    view.setUint16(2, reg2, false); // Big endian for LSW

    return view.getFloat32(0, false); // Big endian
  }

  /**
   * Format register values for debugging (similar to Python's f"{reg1:04X} {reg2:04X}")
   * @param reg1 First register value
   * @param reg2 Second register value
   * @returns Formatted hex string
   */
  static formatRegistersHex(reg1: number, reg2: number): string {
    return `${reg1.toString(16).padStart(4, '0').toUpperCase()} ${reg2.toString(16).padStart(4, '0').toUpperCase()}`;
  }

  /**
   * Get the current RTU connection status
   */
  public async getConnectionStatus(): Promise<string> {
    try {
      const status = (await invoke('get_rtu_connection_status', {
        portPath: this.portName,
        baudRate: 9600,
        slaveId: this.slaveId
      })) as any;

      // Convert the status enum to a string
      if (status.Connected !== undefined) {
        return 'Connected';
      } else if (status.Disconnected !== undefined) {
        return 'Disconnected';
      } else if (status.Error) {
        return `Error: ${status.Error}`;
      } else if (status.Reconnecting !== undefined) {
        return 'Reconnecting';
      } else {
        return 'Unknown';
      }
    } catch (error) {
      logger.error(`Failed to get RTU connection status: ${error}`);
      return 'Error';
    }
  }

  /**
   * List available serial ports
   */
  public static async listAvailablePorts(): Promise<string[]> {
    try {
      const ports = (await invoke('list_available_ports')) as SerialPortInfo[];
      // Extract just the port names from the SerialPortInfo objects
      return ports.map((port) => port.port_name);
    } catch (error) {
      logger.error(`Failed to list serial ports: ${error}`);
      return [];
    }
  }

  /**
   * Set the port name for this device
   */
  public setPortName(portName: string): void {
    this.portName = portName;
    logger.info(`Updated port name to: ${portName}`);
  }

  /**
   * Set the slave ID for this device
   */
  public setSlaveId(slaveId: number): void {
    this.slaveId = slaveId;
    logger.info(`Updated slave ID to: ${slaveId}`);
  }

  /**
   * Update both port and slave ID at once
   */
  public updateConfiguration(portName: string, slaveId: number): void {
    this.portName = portName;
    this.slaveId = slaveId;
    logger.info(`Updated configuration - Port: ${portName}, Slave ID: ${slaveId}`);
  }

  /**
   * Get the current port name
   */
  public getPortName(): string {
    return this.portName;
  }

  /**
   * Get the current slave ID
   */
  public getSlaveId(): number {
    return this.slaveId;
  }

  /**
   * Get the current configuration for debugging
   */
  public getConfiguration(): { portName: string; slaveId: number } {
    return {
      portName: this.portName,
      slaveId: this.slaveId
    };
  }

  /**
   * Start background monitoring for specific registers with custom interval
   * @param registers Array of register addresses to monitor
   * @param intervalMs Read interval in milliseconds (default: 1000ms)
   */
  public async startBackgroundMonitoring(
    registers?: number[],
    intervalMs?: number
  ): Promise<boolean> {
    try {
      const registersToMonitor = registers || [
        SanFranciscanRTUDevice.REGISTERS.ET_REGISTER, // 360 - Environmental Temperature
        SanFranciscanRTUDevice.REGISTERS.ET_REGISTER + 1, // 361 - Environmental Temperature (MSW)
        SanFranciscanRTUDevice.REGISTERS.BT_REGISTER, // 450 - Bean Temperature
        SanFranciscanRTUDevice.REGISTERS.BT_REGISTER + 1 // 451 - Bean Temperature (MSW)
      ];

      await invoke('start_rtu_read_loop', {
        portPath: this.portName,
        baudRate: 9600,
        slaveId: this.slaveId,
        registers: registersToMonitor,
        intervalMs: intervalMs || 1000
      });

      logger.info(
        `Started background monitoring for registers: ${registersToMonitor.join(', ')} with ${intervalMs || 1000}ms interval`
      );
      return true;
    } catch (error) {
      logger.error(`Failed to start background monitoring: ${error}`);
      return false;
    }
  }

  /**
   * Stop background monitoring
   */
  public async stopBackgroundMonitoring(): Promise<boolean> {
    try {
      await invoke('stop_rtu_read_loop', {
        portPath: this.portName,
        baudRate: 9600,
        slaveId: this.slaveId
      });

      logger.info('Stopped background monitoring');
      return true;
    } catch (error) {
      logger.error(`Failed to stop background monitoring: ${error}`);
      return false;
    }
  }

  /**
   * Test connection by reading a simple register (similar to Python script's test_connection)
   * @returns True if device responds, false otherwise
   */
  public async testConnection(): Promise<boolean> {
    try {
      // Try to read a small number of registers (register 0, count 1)
      const results: number[] = await invoke('read_rtu', {
        portPath: this.portName,
        baudRate: 9600,
        slaveId: this.slaveId,
        reg: 0,
        count: 1
      });

      if (modbusDebugEnabled.current === true) {
        logger.debug(
          `Connection test successful: device responded with register 0 = ${results[0]}`
        );
      }

      return true;
    } catch (error) {
      if (modbusDebugEnabled.current === true) {
        logger.debug(`Connection test failed: ${error}`);
      }
      return false;
    }
  }
}
